/**
 * Apply JSONB translations to a content item at display time.
 *
 * `translations` shape (stored in DB):
 * {
 *   "sk": { "title": "...", "body": "...", "instructions": "...", "tip": "..." },
 *   "de": { "title": "..." },
 *   ...
 * }
 *
 * Rules:
 * - If locale is 'en', return item unchanged (EN is the source of truth).
 * - If a translation exists for the locale, overlay only the fields that are present.
 * - If no translation exists for the locale, return null (caller must filter out).
 *   Exception: personal user themes are always returned (caller passes allowFallback=true).
 */
export type TranslatedFields = {
  title?: string | null;
  body?: string | null;
  instructions?: string | null;
  tip?: string | null;
};

export type WithTranslations = {
  translations?: Record<string, TranslatedFields> | null;
};

export function getLocalisedContent<T extends WithTranslations>(
  item: T,
  locale: string,
  allowFallback = false,
): T | null {
  // English is the base — return as-is, no translation overlay needed
  if (locale === 'en') return item;

  const localeTranslations = item.translations?.[locale];

  // No translation available
  if (!localeTranslations) {
    return allowFallback ? item : null;
  }

  // Overlay translated fields, keeping original values for fields not translated
  return {
    ...item,
    ...(localeTranslations.title != null && { title: localeTranslations.title }),
    ...(localeTranslations.body != null && { body: localeTranslations.body }),
    ...(localeTranslations.instructions != null && { instructions: localeTranslations.instructions }),
    ...(localeTranslations.tip != null && { tip: localeTranslations.tip }),
  };
}
